import { type FC, type PropsWithChildren } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { $accessToken, getTokens, logoutSucceeded, setTokens } from '~/entities/auth';
import { setTokenProvider, type TokenProvider } from '~/shared/api/token-provider';
import { ResponsiveModalsProvider } from '~/shared/ui/ResponsiveModals';
import { theme } from './theme';
import { Router } from './router';
import { responsiveModalsMap } from './responsive-modals';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import './styles/globals.css';
import './styles/variables.css';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60_000,
            refetchOnWindowFocus: false,
        },
    },
});

const tokenProvider: TokenProvider = {
    getAccessToken: async () => {
        const storeAccessToken = $accessToken.getState();

        if (storeAccessToken) {
            return storeAccessToken;
        }

        const { accessToken } = await getTokens();

        return accessToken;
    },
    updateAccessToken: async (accessToken) => {
        const tokens = await getTokens();

        await setTokens({
            ...tokens,
            accessToken,
        });
    },
    onUnauthorized: () => {
        logoutSucceeded();
    },
};

setTokenProvider(tokenProvider);

export const Root: FC<PropsWithChildren> = () => {
    return (
        <MantineProvider
            theme={theme}
            defaultColorScheme='auto'
        >
            <Notifications />

            <QueryClientProvider client={queryClient}>
                <ResponsiveModalsProvider modals={responsiveModalsMap}>
                    <Router />
                </ResponsiveModalsProvider>
            </QueryClientProvider>
        </MantineProvider>
    );
};
